import type { BookDto } from '../../../types/book';
import { BookFormat, BookStatus } from '../../../types/book';
import type { BookFilterState } from '../../../types/manageBooks';
import { resolveCoverUrl } from '../../../types/manageBooks';
import { EpubRendererType } from '../../../types/manageBooks';
import { RendererCell } from './RendererCell';
import { SwitchRendererAction } from './SwitchRendererAction';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  rows: BookDto[];
  totalCount: number;
  loading: boolean;
  filters: BookFilterState;
  selection: Map<string, BookDto>;
  onToggleRow: (book: BookDto) => void;
  onTogglePage: (all: boolean) => void;
  onSortChange: (column: string) => void;
  onPageChange: (page: number) => void;
  onRowClick: (book: BookDto) => void;
  /** True when the current user holds learnleaf.App.SwitchBookRenderer. */
  canSwitchRenderer: boolean;
  /**
   * Book whose SwitchRendererAction should auto-open its confirm dialog
   * next render — used when the drawer triggers the switch flow. Parent
   * clears this via `onSwitchDialogOpened` once the dialog opens.
   */
  pendingSwitchBookId: string | null;
  onSwitchDialogOpened: (bookId: string) => void;
}

/**
 * Desktop admin table for the Manage Books page.
 *
 * Presentational only — all data fetching, selection state, and filter state
 * is owned by the parent page. The component surfaces user intent via the
 * callback props.
 */
export function BookListTable({
  rows,
  totalCount,
  loading,
  filters,
  selection,
  onToggleRow,
  onTogglePage,
  onSortChange,
  onPageChange,
  onRowClick,
  canSwitchRenderer,
  pendingSwitchBookId,
  onSwitchDialogOpened,
}: Props) {
  const { t } = useTranslation();
  const allPageSelected = rows.length > 0 && rows.every(r => selection.has(r.id));
  const totalPages = Math.max(1, Math.ceil(totalCount / filters.pageSize));

  // Unicode escapes are fine in JS string literals — these resolve before
  // being placed in JSX.
  const sortIndicator = (col: string) =>
    filters.sort === col ? (filters.dir === 'asc' ? ' \u25B2' : ' \u25BC') : '';

  const ariaSortFor = (col: string): 'ascending' | 'descending' | 'none' => {
    if (filters.sort !== col) return 'none';
    return filters.dir === 'asc' ? 'ascending' : 'descending';
  };

  const statusLabel = (status: BookStatus | undefined): string => {
    switch (status) {
      case BookStatus.Draft: return t('App.Common.BookStatus.Draft');
      case BookStatus.Published: return t('App.Common.BookStatus.Published');
      case BookStatus.Archived: return t('App.Common.BookStatus.Archived');
      default: return String(status ?? '');
    }
  };

  const formatLabel = (format: BookFormat | undefined): string => {
    switch (format) {
      case BookFormat.Epub: return t('App.Common.Format.EPUB');
      case BookFormat.Pdf: return t('App.Common.Format.PDF');
      default: return String(format ?? '');
    }
  };

  return (
    <div>
      <div className="overflow-x-auto border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-100">
            <tr>
              <th className="p-2 w-8">
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={e => onTogglePage(e.target.checked)}
                  aria-label={t('App.Admin.Books.BookListTable.SelectAllAriaLabel')}
                  className="min-w-[20px] min-h-[20px]"
                />
              </th>
              <th className="p-2 w-12" scope="col">{t('App.Admin.Books.BookListTable.ColCover')}</th>
              <th
                className="p-2 text-left cursor-pointer select-none min-h-[44px]"
                onClick={() => onSortChange('title')}
                scope="col"
                aria-sort={ariaSortFor('title')}
              >
                {t('App.Admin.Books.BookListTable.ColTitle')}{sortIndicator('title')}
              </th>
              <th
                className="p-2 text-left cursor-pointer select-none min-h-[44px]"
                onClick={() => onSortChange('author')}
                scope="col"
                aria-sort={ariaSortFor('author')}
              >
                {t('App.Admin.Books.BookListTable.ColAuthor')}{sortIndicator('author')}
              </th>
              <th className="p-2 text-left" scope="col">{t('App.Admin.Books.BookListTable.ColPublisher')}</th>
              <th className="p-2 text-left" scope="col">{t('App.Admin.Books.BookListTable.ColFormat')}</th>
              <th className="p-2 text-left" scope="col">{t('App.Admin.Books.BookListTable.ColStatus')}</th>
              <th className="p-2 text-left" scope="col">{t('App.Admin.Books.BookListTable.ColDRM')}</th>
              <th
                className="p-2 text-left cursor-pointer select-none min-h-[44px]"
                onClick={() => onSortChange('creationTime')}
                scope="col"
                aria-sort={ariaSortFor('creationTime')}
              >
                {t('App.Admin.Books.BookListTable.ColCreated')}{sortIndicator('creationTime')}
              </th>
              <th className="p-2 text-left" scope="col">{t('App.Admin.Books.BookListTable.ColRenderer')}</th>
              <th className="p-2 text-left" scope="col">{t('App.Admin.Books.BookListTable.ColActions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={11} className="p-4 text-center text-neutral-500">{t('App.Admin.Books.BookListTable.Loading')}</td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={11} className="p-4 text-center text-neutral-500">
                  {t('App.Admin.Books.BookListTable.NoResults')}
                </td>
              </tr>
            )}
            {rows.map(b => {
              const cover = resolveCoverUrl(b.coverImageUrl);
              const selected = selection.has(b.id);
              const created = b.creationTime;
              const deletedClass = b.isDeleted ? 'opacity-60 line-through decoration-red-400' : '';
              return (
                <tr
                  key={b.id}
                  className={`hover:bg-neutral-50 cursor-pointer ${selected ? 'bg-primary-50' : ''} ${deletedClass}`}
                  onClick={() => onRowClick(b)}
                >
                  <td className="p-2" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => onToggleRow(b)}
                      aria-label={t('App.Admin.Books.BookListTable.SelectRowAriaLabel', { title: b.title })}
                      className="min-w-[20px] min-h-[20px]"
                    />
                  </td>
                  <td className="p-2">
                    {cover
                      ? <img src={cover} alt="" className="w-10 h-14 object-cover" />
                      : <div className="w-10 h-14 bg-neutral-200" aria-hidden="true" />}
                  </td>
                  <td className="p-2 font-medium">{b.title}</td>
                  <td className="p-2">{b.author}</td>
                  <td className="p-2 font-mono text-xs text-neutral-500">
                    {b.publisherId ? b.publisherId.slice(0, 8) : '\u2014'}
                  </td>
                  <td className="p-2">{formatLabel(b.format)}</td>
                  <td className="p-2">{statusLabel(b.status)}</td>
                  <td className="p-2">{b.isDrmEnabled ? t('App.Common.On') : t('App.Common.Off')}</td>
                  <td className="p-2 text-neutral-600">
                    {created ? new Date(created).toLocaleDateString() : '\u2014'}
                  </td>
                  <td className="p-2">
                    <RendererCell rendererType={(b.rendererType as EpubRendererType) ?? EpubRendererType.CustomHtml} />
                  </td>
                  <td className="p-2" onClick={e => e.stopPropagation()}>
                    <SwitchRendererAction
                      bookId={b.id}
                      bookTitle={b.title ?? ''}
                      bookFormat={b.format as BookFormat}
                      currentRenderer={(b.rendererType as EpubRendererType) ?? EpubRendererType.CustomHtml}
                      allowed={canSwitchRenderer}
                      externalTriggerBookId={pendingSwitchBookId}
                      onDialogOpenedFor={onSwitchDialogOpened}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3 text-sm">
        <span className="text-neutral-600">
          {t('App.Admin.Books.BookListTable.PageSummary', { count: totalCount, page: filters.page, totalPages, totalCount })}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={filters.page <= 1}
            onClick={() => onPageChange(filters.page - 1)}
            className="px-3 py-1 border rounded disabled:opacity-50 min-h-[44px]"
          >
            {t('App.Admin.Books.BookListTable.PrevButton')}
          </button>
          <button
            type="button"
            disabled={filters.page >= totalPages}
            onClick={() => onPageChange(filters.page + 1)}
            className="px-3 py-1 border rounded disabled:opacity-50 min-h-[44px]"
          >
            {t('App.Admin.Books.BookListTable.NextButton')}
          </button>
        </div>
      </div>
    </div>
  );
}
